/**
 * TranscriptionDiffTranslated.tsx
 *
 * Translated transcript screen with redline (out-of-scope content visible
 * with strike-through). Parallel implementation to `_TranscriptionDiff`
 * (the original-language redline view) — same role for translated text
 * that `TranscriptionDiffNew` plays for the original.
 *
 * Data shaping is shared with `TranscriptionTextTranslated` via
 * `useTranslatedRender` (with `includeOutOfScope: true`); this component
 * only owns the JSX and redline-mode CSS classes.
 *
 * Phrase state aggregation rule (in the shared hook): worst state across
 * source members wins (`removed > cut > muted > kept`). A mixed-state
 * phrase takes the worst state's styling. A future iteration could
 * surface a "partial" indicator; for now the conservative aggregation
 * matches what users expect when reading a redlined transcript.
 */

import { useSpeakerLabels } from '@src/hooks/useSpeakerLabels';
import { formatLanguageCaption } from '@src/utility/languageCaption';
import { overlayHighlightStyles } from '@src/utility/transcriptQuoteUtils';
import { observer } from 'mobx-react-lite';
import React, { useRef } from 'react';
import styled, { keyframes } from 'styled-components';

import { usePhraseEditing } from './hooks/usePhraseEditing';
import {
  type TranslatedSortedPhrase,
  useTranslatedRender,
} from './hooks/useTranslatedRender';
import {
  CURSOR_ALLOWANCE_PX,
  topicRailCss,
  topicRailProps,
} from './topicRailStyles';
import { renderPhrasesWithUnderline } from './translatedOverlayUnderline';
import { TranslatedPhraseContextMenu } from './TranslatedPhraseContextMenu';
import { resolveSourceText } from './translatedPhraseEditing';
import { TranslatedPhraseEditorPopover } from './TranslatedPhraseEditorPopover';
import { isPhraseInTranslatedSelection } from './translatedSelectionGuard';

import type { TranscriptElement } from '@src/types.ts/transcript';
import type { TranslationPhrase } from '@src/types.ts/translation';
import type { HighlightRange } from '@src/utility/transcriptQuoteUtils';

interface TranscriptionDiffTranslatedProps {
  language: string;
  transcriptElements: TranscriptElement[] | undefined;
  currentTime?: number;
  isPlaying?: boolean;
  /** Enables right-click "Edit translation" menu. Default true. */
  editable?: boolean;
  onWordClick?: (initialIndex: number) => void;
  /**
   * When set, only paragraphs whose paragraph_id is in this set render.
   * Speaker headers / timestamp rows for filtered paragraphs are dropped
   * along with them.
   */
  visibleParagraphIds?: ReadonlySet<string>;
  /**
   * When set, paragraphs whose paragraph_id is in this set get the topic
   * rail in the left gutter (does not filter the render).
   */
  highlightedParagraphIds?: ReadonlySet<string>;
  /**
   * Per-paragraph rail colors keyed by paragraph_id. Falls back to the
   * default blue when missing.
   */
  paragraphHighlightColors?: ReadonlyMap<string, string>;
  /**
   * Paragraph_id of the currently-active topic match — gets a solid rail
   * while the other matches stay muted. Optional.
   */
  activeParagraphId?: string;
  /**
   * Overlay ranges in phrase-lead (`data-initial-index`) space. Phrases whose
   * `lead` falls in a range get a CONTINUOUS render-time underline — runs of
   * in-range phrases (and the whitespace between them) are wrapped in one
   * `OverlayUnderline`, instead of the imperative per-phrase `overlay-match`
   * class which left gaps at the inter-phrase whitespace and broke across
   * struck words. The redline view collapses text differently from the regular
   * view, so it needs this separate underline algorithm.
   */
  overlayRanges?: HighlightRange[];
}

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs
    .toString()
    .padStart(2, '0')}`;
};

export const TranscriptionDiffTranslated: React.FC<TranscriptionDiffTranslatedProps> =
  observer(
    ({
      language,
      transcriptElements,
      currentTime,
      isPlaying,
      editable = true,
      onWordClick,
      visibleParagraphIds,
      highlightedParagraphIds,
      paragraphHighlightColors,
      activeParagraphId,
      overlayRanges,
    }) => {
      const { getSpeakerName, getSpeakerColor } = useSpeakerLabels();
      const {
        contextMenu,
        editing,
        editingCurrentValue,
        editingOriginalValue,
        editingIsEdited,
        handleContextMenuRequest,
        handleMenuEdit,
        handleMenuClose,
        handleSave,
        handleCancel,
        handleRevert,
      } = usePhraseEditing(language, editable);

      const {
        translation,
        groups,
        phrasesByGroup,
        currentLead,
        groupIsAllDeleted,
      } = useTranslatedRender(language, transcriptElements, currentTime, {
        includeOutOfScope: true,
      });

      // PLAT-10467 — this view's own word container, for column-scoped selection
      // resolution in the right-click precedence guard (CLAUDE.md #12).
      const wrapperRef = useRef<HTMLDivElement>(null);

      if (!translation) {
        return <Empty>No translation data available for {language}.</Empty>;
      }

      // Recompute isNewSpeaker against the post-filter group sequence; same
      // rationale as TranscriptionTextTranslated.
      let prevVisibleSpeaker: string | undefined;
      const visibleGroups = groups.map((group, gi) => {
        const inScope =
          !visibleParagraphIds ||
          (group.paragraphId !== undefined &&
            visibleParagraphIds.has(group.paragraphId));
        if (!inScope) return null;
        const phrases = phrasesByGroup[gi] ?? [];
        if (phrases.length === 0) return null;
        const isNewSpeaker = group.speakerLabel !== prevVisibleSpeaker;
        prevVisibleSpeaker = group.speakerLabel;
        return {
          group,
          gi,
          phrases,
          isNewSpeaker,
          allDeleted: groupIsAllDeleted[gi],
        };
      });

      return (
        <Wrapper ref={wrapperRef}>
          {visibleGroups.map(v => {
            if (!v) return null;
            const { group, gi, phrases, isNewSpeaker, allDeleted } = v;
            return (
              <React.Fragment
                key={`group-${gi}-${group.paragraphId ?? 'noid'}`}
              >
                {isNewSpeaker && group.speakerLabel && (
                  <SpeakerHeader
                    contentEditable={false}
                    suppressContentEditableWarning
                  >
                    <SpeakerLabel
                      $color={getSpeakerColor(group.speakerLabel)}
                      $isDeleted={allDeleted}
                    >
                      {getSpeakerName(group.speakerLabel)}
                    </SpeakerLabel>
                    {group.startTime !== undefined && (
                      <SpeakerTime>{formatTime(group.startTime)}</SpeakerTime>
                    )}
                  </SpeakerHeader>
                )}
                {!isNewSpeaker && group.startTime !== undefined && (
                  <TimestampRow
                    contentEditable={false}
                    suppressContentEditableWarning
                  >
                    {formatTime(group.startTime)}
                  </TimestampRow>
                )}
                <ParagraphWrapper
                  data-paragraph-id={group.paragraphId}
                  {...topicRailProps(
                    group.paragraphId,
                    highlightedParagraphIds,
                    activeParagraphId,
                    paragraphHighlightColors,
                  )}
                >
                  {renderPhrasesWithUnderline(phrases, overlayRanges, (p, pi) =>
                    renderPhrase(
                      p,
                      {
                        onWordClick,
                        isCurrent: p.lead === currentLead,
                        isPlaying: !!isPlaying,
                        editable,
                        onContextMenuRequest: handleContextMenuRequest,
                        // Selection precedence (PLAT-10467): resolve the
                        // selection scoped to this view's own container.
                        selectionContainerRef: wrapperRef,
                        allPhrases: translation?.phrases,
                      },
                      pi,
                    ),
                  )}
                </ParagraphWrapper>
              </React.Fragment>
            );
          })}
          {contextMenu && (
            <TranslatedPhraseContextMenu
              positionXY={contextMenu.positionXY}
              onEdit={handleMenuEdit}
              onClose={handleMenuClose}
            />
          )}
          {editing && (
            <TranslatedPhraseEditorPopover
              anchorElement={editing.anchorElement}
              sourceText={resolveSourceText(
                editing.phrase,
                transcriptElements ?? [],
              )}
              sourceLanguageLabel={
                translation.sourceLanguage
                  ? formatLanguageCaption(translation.sourceLanguage)
                  : undefined
              }
              originalValue={editingOriginalValue}
              currentValue={editingCurrentValue}
              onSave={value => handleSave(editing.phrase, value)}
              onCancel={handleCancel}
              canRevert={editingIsEdited}
              onRevert={() => handleRevert(editing.phrase)}
            />
          )}
        </Wrapper>
      );
    },
  );

interface RenderPhraseOpts {
  onWordClick: ((initialIndex: number) => void) | undefined;
  isCurrent: boolean;
  isPlaying: boolean;
  editable: boolean;
  onContextMenuRequest: (
    phrase: TranslationPhrase,
    anchorElement: HTMLElement,
    positionXY: { x: number; y: number },
  ) => void;
  /** PLAT-10467 — this view's own word container, for column-scoped selection
   *  resolution (CLAUDE.md #12 — never `document`). */
  selectionContainerRef: React.RefObject<HTMLElement>;
  /** The surface's phrases; the guard filters to the active set. */
  allPhrases: readonly TranslationPhrase[] | undefined;
}

function renderPhrase(
  p: TranslatedSortedPhrase,
  opts: RenderPhraseOpts,
  // Map index — `p.lead` alone isn't unique when data has duplicate-lead phrases.
  keyIndex: number,
): React.ReactNode {
  const value = p.phrase.translatedValue ?? '';
  const isWhitespaceOnly = value.trim() === '';
  const isInteractive =
    p.effectiveState !== 'cut' && p.effectiveState !== 'removed';
  const classes: string[] = [];
  if (isWhitespaceOnly) classes.push('whitespace');
  if (p.effectiveState !== 'kept') classes.push(`state-${p.effectiveState}`);
  if (opts.isCurrent && isInteractive) {
    classes.push('current');
    if (!opts.isPlaying) classes.push('cursor-before');
  }
  const handleContextMenu = (e: React.MouseEvent<HTMLSpanElement>) => {
    if (!opts.editable || isWhitespaceOnly || !isInteractive) return;
    // Selection precedence (PLAT-10467): a non-empty selection covering this
    // word means the selection owns the edit — suppress the single-word menu
    // (preventDefault + return, no menu). Uniform with the other translated
    // surfaces' precedence rule.
    if (
      isPhraseInTranslatedSelection(
        opts.selectionContainerRef.current,
        opts.allPhrases,
        p.phrase,
      )
    ) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    opts.onContextMenuRequest(p.phrase, e.currentTarget, {
      x: e.clientX,
      y: e.clientY,
    });
  };
  return (
    <Word
      key={`p-${p.lead}-${keyIndex}`}
      data-initial-index={p.lead}
      // PLAT-10611 (F5) — stamp the lead element's stable UUID beside the
      // retained integer lead (same value the cross-language resolver reads);
      // omitted when the lead has no id.
      data-source-uuid={p.leadUuid}
      className={classes.length > 0 ? classes.join(' ') : undefined}
      onClick={
        opts.onWordClick && !isWhitespaceOnly && isInteractive
          ? () => opts.onWordClick!(p.lead)
          : undefined
      }
      onContextMenu={handleContextMenu}
    >
      {value}
    </Word>
  );
}

/* Cursor allowance only — the topic-rail gutter lives on the scroll container
   so the text lines up with the toolbar (PLAT-11039). */
const Wrapper = styled.div`
  padding-left: ${CURSOR_ALLOWANCE_PX}px;
`;

const ParagraphWrapper = styled.div`
  display: block;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }

  ${topicRailCss}
`;

const SpeakerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2px;
  margin-top: 10px;
  user-select: none;
  pointer-events: none;

  &:first-child {
    margin-top: 0;
  }
`;

const SpeakerLabel = styled.span<{ $color?: string; $isDeleted?: boolean }>`
  color: ${props => props.$color || 'var(--accent)'};
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  ${props => props.$isDeleted && `text-decoration: line-through;`}
`;

const SpeakerTime = styled.span`
  color: var(--text-muted);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-regular);
`;

const TimestampRow = styled.div`
  text-align: right;
  color: var(--text-muted);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-regular);
  line-height: 1;
  margin-top: -11px;
  margin-bottom: 5px;
  user-select: none;
  pointer-events: none;
`;

const cursorAnimation = keyframes`
  100% {
    opacity: 0;
  }
`;

const Word = styled.span`
  color: var(--text-secondary);
  position: relative;
  cursor: pointer;

  /* PLAT-10066 — overlay underline + hover state for translated redline
     phrase spans. Shared with TranscriptionTextTranslated so resting /
     hovered styling stays in sync. */
  ${overlayHighlightStyles}

  &.whitespace {
    white-space: pre-wrap;
    cursor: default;
  }

  /* Redline state styling — colors mirror _TranscriptionDiff so the
     translated and original-language redline views share visual language. */
  &.state-removed {
    color: #cd5c5c;
    text-decoration: line-through #cd5c5c80;
    cursor: default;
  }
  &.state-cut {
    color: #7bb97599;
    text-decoration: line-through #7bb97580;
    cursor: default;
  }
  &.state-muted {
    color: #f69b12;
  }

  &.current {
    color: black;
    background-color: #dfb615;
  }

  &.current.cursor-before {
    color: var(--text-secondary);
    background-color: transparent;
  }
  /* Leading cursor: spans the FULL line-height of the active word (top:0
     → bottom:0 on the relatively-positioned Word). Mirrors the English
     redline's TranscriptLeadingCursorOverlay which uses
     height: rect.height. */
  &.current.cursor-before::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: -3px;
    width: 2px;
    background-color: #dfb615;
    border-radius: 1px;
    animation: ${cursorAnimation} steps(2) 1.5s infinite;
    will-change: opacity;
  }

  /* Hover declared last + !important so it wins over .current /
     .current.cursor-before for the playhead phrase. Mirrors WordLight
     in TranscriptionText.tsx. */
  &:hover:not(.state-cut):not(.state-removed) {
    color: lightgreen !important;
  }
`;

const Empty = styled.div`
  color: var(--text-muted);
  font-size: var(--text-xs);
  padding: 20px;
  text-align: center;
`;
